import { useMemo } from 'react';

import { Icon } from '@rbilabs/universal-components';
import { useAppTakeoverPdp } from 'features/app-takeover/hooks/use-app-takeover';
import { isDummyItem } from 'features/cart/utils/minimal-cart';
import { mapModifier } from 'features/menu/components/product-detail/modifier/utils';
import {
  ProductComboFlowContainer,
  ProductComboFlowInnerLayout,
} from 'features/menu/components/product-detail/product-combo/styled';
import { isCustomizableEntry } from 'features/menu/components/product-detail/product-customization/utils';
import { useProductWizardContext } from 'features/menu/components/product-wizard';
import {
  DEFAULT_SLOT_KEY,
  ModifiersSelections,
  ProductWizardStep,
  SlotSelections,
  SlotsSelections,
} from 'features/menu/components/product-wizard/types';
import { useProductWizardFlow } from 'features/menu/components/product-wizard/use-product-wizard-flow';
import { computeMenuItemPrice } from 'features/menu/components/product-wizard/utils';
import { useStoreContext } from 'features/store';

import { ProductDetailListItem } from 'components/product-detail-list-item';
import { ProductDetailListItemGroup } from 'components/product-detail-list-item-group';
import { ISelections, ProductProps } from 'components/product-detail-list-item/product/types';
import { IProductDetailListItemProps } from 'components/product-detail-list-item/types';
import { ItemAvailabilityStatus } from 'enums/menu';
import { EntryTypeEnum, IPriceDto } from 'generated/graphql-gateway';
import { FormatCaloriesFn, useMenuCalories } from 'hooks/menu/use-menu-calories';
import { ClickEventComponentNames, logButtonClick } from 'state/amplitude';
import { useMainMenuContext } from 'state/menu/main-menu';
import { IDayPartBoundary } from 'state/menu/main-menu/types';
import { ILocalizedMenuEntry, IndexedMenu, PosServiceMode } from 'state/unified-menu/types';
import { useUnifiedMenuContext } from 'state/unified-menu/unified-menu-context';
import { useMenuQueryOptions } from 'state/unified-menu/use-menu-query-options';
import {
  getComboSlotItemPrice,
  getFirstDefaultId,
  hasAvailableComboSlotOptions,
  hasOptionEntries,
  isCombo,
  isEntryAvailable,
  isItem,
  mapImageResourceToSanityImage,
} from 'state/unified-menu/utils';
import { getOneMenuAvailability } from 'utils/availability/one-menu-availability';

import { ModifierType } from '../modifier/types';

export const ProductCombo: React.FC = () => {
  const { formatCalories } = useMenuCalories();
  const { indexedMenu } = useUnifiedMenuContext();
  const { posServiceMode } = useMenuQueryOptions();
  const { selectedProduct, slotsSelections } = useProductWizardContext();
  const { goToCustomization, id, currentFlowStep } = useProductWizardFlow();
  const { fontFamily: takeoverFontFamily } = useAppTakeoverPdp(id);
  const { activeDayParts } = useMainMenuContext();
  const { store } = useStoreContext();
  const storeHasBurgersForBreakfast = !!store.hasBurgersForBreakfast;

  const mappedItems = useMemo(
    () =>
      createProductDetailListItemProps(
        indexedMenu,
        posServiceMode,
        selectedProduct,
        slotsSelections,
        formatCalories,
        goToCustomization,
        takeoverFontFamily ?? '',
        activeDayParts,
        currentFlowStep.step,
        storeHasBurgersForBreakfast
      ),
    [
      indexedMenu,
      posServiceMode,
      selectedProduct,
      slotsSelections,
      formatCalories,
      goToCustomization,
      takeoverFontFamily,
      activeDayParts,
      currentFlowStep.step,
      storeHasBurgersForBreakfast,
    ]
  );

  return (
    <ProductComboFlowContainer testID="product-combo-flow" key={1}>
      <ProductComboFlowInnerLayout>
        <ProductDetailListItemGroup
          items={mappedItems.map(({ key, ...others }) => (
            <ProductDetailListItem {...others} key={key} />
          ))}
        />
      </ProductComboFlowInnerLayout>
    </ProductComboFlowContainer>
  );
};

interface IProductDetailListItemPropsWithKey extends IProductDetailListItemProps {
  key: string;
}

interface IRow {
  item: ILocalizedMenuEntry;
  contextualPrice?: IPriceDto;
  defaultContextPrice?: IPriceDto;
  slotSelection?: SlotSelections;
  slotKey: string;
  isCustomizable: boolean;
}

function sortAndUpdateNameByQuantity(items: ISelections[]): ISelections[] {
  if (!items) {
    return items;
  }

  return [...items]
    .sort((a, b) => {
      return (b.quantity ?? 0) - (a.quantity ?? 0);
    })
    .map(item => {
      return {
        ...item,
        name:
          item.quantity > 1 &&
          (item.type === ModifierType.SINGLE_CHOICE || item.type === ModifierType.MULTIPLE_CHOICE)
            ? `${item.quantity} ${item.name}`
            : item.name,
      };
    });
}

function createProductDetailListItemProps(
  indexedMenu: IndexedMenu<ILocalizedMenuEntry>,
  posServiceMode: PosServiceMode,
  selectedProduct: ILocalizedMenuEntry,
  slotsSelections: SlotsSelections,
  formatCalories: FormatCaloriesFn,
  goToCustomization: ReturnType<typeof useProductWizardFlow>['goToCustomization'],
  fontFamily: string,
  activeDayParts: IDayPartBoundary[],
  currentFlowStep: ProductWizardStep,
  storeHasBurgersForBreakfast: boolean
): IProductDetailListItemPropsWithKey[] {
  const result: IProductDetailListItemPropsWithKey[] = [];

  const rows: IRow[] = [];

  if (isCombo(selectedProduct)) {
    for (const comboSlotRef of selectedProduct?.options?.entries ?? []) {
      const comboSlot = indexedMenu.get(comboSlotRef.entryId);
      const slotSelection = slotsSelections[comboSlot.id];

      const slotOptions = comboSlot.options?.entries ?? [];

      const availableItems = slotOptions
        .map(opt => indexedMenu.get(opt.entryId))
        .filter(entry => {
          const availabilityStatus = getOneMenuAvailability(
            entry,
            activeDayParts,
            posServiceMode,
            storeHasBurgersForBreakfast,
            isEntryAvailable
          );
          return availabilityStatus === ItemAvailabilityStatus.AVAILABLE;
        });

      const defaultItemId = getFirstDefaultId(comboSlot);
      const defaultComboSlotOption = availableItems.find(entry => entry.id === defaultItemId);
      const hasAvailableOptionFallback = hasAvailableComboSlotOptions(
        slotOptions.length,
        availableItems.length,
        defaultComboSlotOption
      );

      let contextualPrice;
      let defaultContextPrice;
      let item: ILocalizedMenuEntry;

      if (hasAvailableOptionFallback) {
        item = availableItems[0];
        contextualPrice = getComboSlotItemPrice(indexedMenu, comboSlot.id, item.id);
        defaultContextPrice = getComboSlotItemPrice(indexedMenu, comboSlot.id, undefined);
      } else if (slotSelection?.itemId) {
        item = indexedMenu.get(slotSelection.itemId, { type: EntryTypeEnum.ITEM });
        contextualPrice = getComboSlotItemPrice(indexedMenu, comboSlot.id, slotSelection.itemId);
        defaultContextPrice = getComboSlotItemPrice(indexedMenu, comboSlot.id, undefined);
      } else {
        item = indexedMenu.get(defaultItemId);
        const defaultPrice = getComboSlotItemPrice(indexedMenu, comboSlot.id, undefined);
        contextualPrice = defaultPrice;
        defaultContextPrice = defaultPrice;
      }

      rows.push({
        item,
        contextualPrice,
        defaultContextPrice,
        slotKey: comboSlot.id,
        slotSelection,
        isCustomizable: isCustomizableEntry(
          comboSlot,
          indexedMenu,
          posServiceMode,
          activeDayParts,
          storeHasBurgersForBreakfast
        ),
      });
    }
  } else if (isItem(selectedProduct)) {
    rows.push({
      item: selectedProduct,
      contextualPrice: selectedProduct.price ?? undefined,
      defaultContextPrice: selectedProduct.price ?? undefined,
      slotKey: DEFAULT_SLOT_KEY,
      slotSelection: slotsSelections[DEFAULT_SLOT_KEY],
      isCustomizable: isCustomizableEntry(
        selectedProduct,
        indexedMenu,
        posServiceMode,
        activeDayParts,
        storeHasBurgersForBreakfast
      ),
    });
  }

  if (!rows.length) {
    return result;
  }

  for (const row of rows) {
    const {
      item,
      contextualPrice,
      defaultContextPrice,
      slotSelection,
      slotKey,
      isCustomizable,
    } = row;

    const productName = item.name?.locale ?? item.id;

    if (isDummyItem(productName)) {
      continue;
    }

    const modifierSelections = slotSelection?.modifiers ?? {};
    const itemPrice = computeMenuItemPrice(
      indexedMenu,
      item,
      posServiceMode,
      contextualPrice,
      modifierSelections
    );
    const defaultPrice = defaultContextPrice?.[posServiceMode] ?? 0;

    // Only show price for upsell combo slots or customizations
    const price = itemPrice === defaultPrice ? undefined : itemPrice - defaultPrice;
    const calories = item.nutrition?.calories?.def ?? 0;
    const selections = getProductSelections(indexedMenu, posServiceMode, item, modifierSelections);

    const imgSrc = item.image?.resource;
    const image = imgSrc ? mapImageResourceToSanityImage(imgSrc) : undefined;
    result.push({
      key: slotKey,
      isClickable: isCustomizable,
      productProps: {
        name: productName,
        calories: formatCalories(calories),
        image: image ? JSON.stringify(image) : undefined,
        isProductNameBold: true,
        price,
        selections,
        fontFamily,
      },
      controls: isCustomizable ? <Icon variant="next" testID="next-icon" /> : undefined,
      onNavigate: () => {
        logButtonClick({
          attributes: {
            component:
              currentFlowStep === ProductWizardStep.ProductHome
                ? ClickEventComponentNames.PRODUCT_HOME_EDIT_ITEM
                : ClickEventComponentNames.PRODUCT_ORDER_EDIT_ITEM,
            name: item?.name?.locale ?? '',
          },
        });
        return goToCustomization(slotKey);
      },
    });
  }

  return result;
}

export function getProductSelections(
  menu: IndexedMenu<ILocalizedMenuEntry>,
  posServiceMode: PosServiceMode,
  entry: ILocalizedMenuEntry,
  modifiersSelections: ModifiersSelections
): ProductProps['selections'] {
  const result: ISelections[] = [];
  if (!hasOptionEntries(posServiceMode, entry)) {
    return result;
  }

  for (const [modifierId, multipliers] of Object.entries(modifiersSelections)) {
    const modifier = mapModifier(
      menu,
      modifierId,
      entry.options?.entries?.length ?? 0,
      posServiceMode
    );

    if (!modifier || !modifier.multipliers?.length) {
      continue;
    }

    for (const [multiplierId, quantity] of Object.entries(multipliers)) {
      const multiplier = modifier.multipliers.find(({ id }) => id === multiplierId);

      if (!multiplier) {
        // This actually can happen
        continue;
      }

      const isSingleOrMultipleChoiceModifier = [
        ModifierType.SINGLE_CHOICE,
        ModifierType.MULTIPLE_CHOICE,
      ].includes(modifier.type);

      const name = isSingleOrMultipleChoiceModifier
        ? multiplier.name
        : `${multiplier.prefix} ${modifier.name}`;

      result.push({
        id: `${modifierId}-${multiplierId}`,
        name,
        calories: multiplier.nutrition?.calories?.def ?? 0,
        multiplier: multiplier.multiplier,
        price: (multiplier.price ?? 0) * quantity,
        quantity,
        type: modifier.type,
      });
    }
  }

  return sortAndUpdateNameByQuantity(result);
}
